import {UserManager, WebStorageStateStore, User} from 'oidc-client-ts';

const clientId =
    import.meta.env.VITE_ENV_MONKIER === 'Dev'
        ? import.meta.env.VITE_PayLOGIKDevClientAppId
        : import.meta.env.VITE_PayLOGIKProdClientAppId;

const authority = import.meta.env.VITE_COGNITO_AUTHORITY;

// Creates a UserManager instance for OIDC
const createUserManager = (): UserManager => {
    const config = {
        authority: authority,
        client_id: clientId,
        redirect_uri: import.meta.env.VITE_REDIRECT_URI,
        post_logout_redirect_uri: import.meta.env.VITE_SIGNOUT_URI,
        response_type: 'code',
        scope: 'openid email profile',
        userStore: new WebStorageStateStore({store: window.localStorage}),
        automaticSilentRenew: true,
        loadUserInfo: true,
    };

    return new UserManager(config);
};

const userManager = createUserManager();

interface UserSession {
    credentials: {
        accessToken: string;
        idToken: string;
        refreshToken: string;
    };
    user: {[key: string]: any};
}

export const oidcUtils = {
    // Get the URI of the hosted sign in screen (not typically used with OIDC as signin is initiated via signinRedirect)
    getSignInUri: async () => {
        // This method is kept for compatibility but OIDC typically uses signinRedirect
        const settings = await userManager.settings;
        return `${settings.authority}/authorize?client_id=${settings.client_id}&redirect_uri=${settings.redirect_uri}&response_type=code&scope=${settings.scope}`;
    },

    // Handle the callback from the OIDC provider
    handleCallback: async (): Promise<User | null> => {
        try {
            const user = await userManager.signinRedirectCallback();
            return user;
        } catch (error) {
            console.error('Error handling callback:', error);
            throw error;
        }
    },

    // Gets the current user session
    getSession: async (): Promise<UserSession | null> => {
        try {
            const user = await userManager.getUser();
            if (!user || user.expired) {
                return null;
            }

            return {
                credentials: {
                    accessToken: user.access_token,
                    idToken: user.id_token,
                    refreshToken: user.refresh_token || '',
                },
                user: user.profile,
            };
        } catch (error) {
            console.error('Error getting session:', error);
            throw error;
        }
    },

    // Sign out of the current session
    signOut: async () => {
        try {
            await userManager.signoutRedirect();
        } catch (error) {
            console.error('Error signing out:', error);
            throw error;
        }
    },

    // Initiate sign in
    signIn: async () => {
        try {
            await userManager.signinRedirect();
        } catch (error) {
            console.error('Error signing in:', error);
            throw error;
        }
    },
};

const getToken = async (): Promise<string | null> => {
    const session = await oidcUtils.getSession();
    return session?.credentials.idToken || null;
};

const OIDCProvider = {
    authUseEffect: (
        onLogin: (
            idToken: string,
            user: {[key: string]: any},
            provider: string
        ) => void,
        onError: (error: any) => void
    ) => {
        // Check if we're returning from a callback
        if (
            window.location.search.includes('code=') ||
            window.location.search.includes('state=')
        ) {
            oidcUtils
                .handleCallback()
                .then((user) => {
                    if (user) {
                        // Clear the callback parameters from URL
                        window.history.replaceState(
                            {},
                            document.title,
                            window.location.pathname
                        );
                        // The onLogin will be called when getSession is called in the next check
                        return oidcUtils.getSession();
                    }
                    return null;
                })
                .then((session) => {
                    if (session) {
                        onLogin(
                            session.credentials.idToken,
                            session.user,
                            'OIDC'
                        );
                    }
                })
                .catch((error) => {
                    console.error('Error in callback:', error);
                    onError(error);
                });
            return;
        }

        // Check for existing session
        oidcUtils
            .getSession()
            .then((session) => {
                if (!session) {
                    // No session, redirect to login
                    oidcUtils.signIn();
                    return;
                }
                onLogin(session.credentials.idToken, session.user, 'OIDC');
            })
            .catch((error) => {
                console.error('Error getting session:', error);
                onError(error);
            });
    },
    getToken: getToken,
    getSession: oidcUtils.getSession,
    logout: oidcUtils.signOut,
    userManager: userManager,
};

export default OIDCProvider;
